import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../../components/BaseUI/Modal";
import Button from "../../../../components/BaseUI/Button";
import Input from "../../../../components/BaseUI/Input";
import Label from "../../../../components/BaseUI/Label";
import Select from "../../../../components/BaseUI/Select";
import { dateStrInAppTz } from "../../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const todayStr = () => dateStrInAppTz(new Date());

const ScrapEntryModal = ({
  isOpen,
  onClose,
  machine,
  initialShiftId,
  initialPartId,
  getToken,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [partId, setPartId] = useState("");
  const [producedParts, setProducedParts] = useState([]);
  const [shift, setShift] = useState("");
  const [scrapGroup, setScrapGroup] = useState("");
  const [date, setDate] = useState(todayStr());
  const [totalProduced, setTotalProduced] = useState(0);
  const [loadingProduced, setLoadingProduced] = useState(false);
  const [currentRejected, setCurrentRejected] = useState(0);
  const [addRejected, setAddRejected] = useState("");
  const [notes, setNotes] = useState("");

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const resetForm = useCallback(() => {
    setFormData(null);
    setError("");
    setPartId("");
    setProducedParts([]);
    setShift(initialShiftId ? String(initialShiftId) : "");
    setScrapGroup("");
    setDate(todayStr());
    setTotalProduced(0);
    setCurrentRejected(0);
    setAddRejected("");
    setNotes("");
  }, [initialShiftId]);

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen, resetForm]);

  const fetchBaseFormData = useCallback(async () => {
    if (!machine?._id) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        machineId: machine._id,
        action: "scrap",
        date: todayStr(),
      });
      const { data } = await axios.get(
        `${API_URL}/api/epc/form-data?${params}`,
        { headers: headers() },
      );
      setFormData(data);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Form verileri yüklenemedi",
      );
    } finally {
      setLoading(false);
    }
  }, [machine?._id, headers]);

  useEffect(() => {
    if (isOpen && machine?._id) {
      fetchBaseFormData();
    }
  }, [isOpen, machine?._id, fetchBaseFormData]);

  const fetchScrapContext = useCallback(
    async (dateVal, shiftVal, partIdVal) => {
      if (!machine?._id || !dateVal) return;
      setLoadingProduced(true);
      try {
        const params = new URLSearchParams({
          machineId: machine._id,
          action: "scrap",
          date: dateVal,
        });
        if (shiftVal) params.set("shift", shiftVal);
        if (partIdVal) params.set("partId", partIdVal);

        const { data } = await axios.get(
          `${API_URL}/api/epc/form-data?${params}`,
          { headers: headers() },
        );

        const partsList = data.producedParts || [];
        setProducedParts(partsList);

        if (partIdVal && !partsList.some((p) => p.partId === partIdVal)) {
          setPartId("");
          setTotalProduced(0);
          setCurrentRejected(0);
          setScrapGroup("");
        } else {
          setTotalProduced(data.totalProduced ?? 0);
          const existing = data.existingScrapEntry;
          if (existing) {
            const groupId =
              existing.scrapGroup?._id || existing.scrapGroup || "";
            setScrapGroup(groupId ? String(groupId) : "");
            setCurrentRejected(Math.max(0, Number(existing.rejected) || 0));
          } else {
            setScrapGroup("");
            setCurrentRejected(0);
          }
        }
        setAddRejected("");
        setNotes("");
      } catch {
        setTotalProduced(0);
      } finally {
        setLoadingProduced(false);
      }
    },
    [machine?._id, headers],
  );

  useEffect(() => {
    if (!isOpen || !date || loading) return;
    fetchScrapContext(date, shift, partId);
  }, [isOpen, date, shift, partId, fetchScrapContext, loading]);

  useEffect(() => {
    if (!isOpen || !initialPartId || partId) return;
    setPartId(String(initialPartId));
  }, [isOpen, initialPartId, partId]);

  const shiftOpts = (formData?.shifts || []).map((s) => ({
    value: s._id,
    label: s.name,
  }));

  const scrapGroupOpts = (formData?.scrapGroups || []).map((g) => ({
    value: g._id,
    label: g.name,
  }));

  const partOpts = producedParts.map((p) => ({
    value: p.partId,
    label: `${p.partName}${p.partCode ? ` (${p.partCode})` : ""} — ${p.quantity} adet`,
  }));

  const addAmount = Math.max(0, Number(addRejected) || 0);
  const canSave =
    !!partId &&
    !!scrapGroup &&
    addAmount > 0 &&
    currentRejected + addAmount <= totalProduced &&
    scrapGroupOpts.length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/epc/scrap`,
        {
          machine: machine._id,
          shift: shift || undefined,
          scrapGroup,
          part: partId,
          date,
          rejected: addAmount,
          notes,
        },
        { headers: headers() },
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Hurda kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const machineLabel = machine?.name || machine?.code || "";

  return (
    <Modal
      isOpen={isOpen}
      width="medium"
      scrollBehavior="inside"
      onClose={saving ? undefined : onClose}
      className="mlm-entry-modal"
    >
      <ModalHeader>
        Hurda Girişi{machineLabel ? ` — ${machineLabel}` : ""}
      </ModalHeader>
      <ModalBody showCloseButton={false}>
        {loading ? (
          <div className="mlm-entry-modal__loading">Form yükleniyor...</div>
        ) : (
          <div className="mlm-entry-form">
            <div className="mlm-entry-form__field">
              <Select
                label="Üretilen Parça / Ürün"
                required
                unique
                disabled={saving || partOpts.length === 0}
                placeholder={
                  partOpts.length === 0
                    ? "Bu gün/vardiya için üretim yok"
                    : "Parça seçiniz"
                }
                options={partOpts}
                selected={partId ? [partId] : []}
                onSelected={(items) =>
                  setPartId(items?.[0]?.value ? String(items[0].value) : "")
                }
              />
            </div>

            <div className="mlm-entry-form__row">
              <div className="mlm-entry-form__field">
                <Select
                  label="Vardiya"
                  unique
                  disabled={saving}
                  placeholder="Vardiya seçiniz"
                  options={shiftOpts}
                  selected={shift ? [shift] : []}
                  onSelected={(items) =>
                    setShift(items?.[0]?.value ? String(items[0].value) : "")
                  }
                />
              </div>
              <div className="mlm-entry-form__field">
                <Label required>Tarih</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={setDate}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="mlm-entry-form__row">
              <div className="mlm-entry-form__field">
                <Select
                  label="Hurda Grubu"
                  required
                  unique
                  disabled={saving || scrapGroupOpts.length === 0}
                  placeholder={
                    scrapGroupOpts.length === 0
                      ? "Tanımlı hurda grubu yok"
                      : "Hurda grubu seçiniz"
                  }
                  options={scrapGroupOpts}
                  selected={scrapGroup ? [scrapGroup] : []}
                  onSelected={(items) =>
                    setScrapGroup(
                      items?.[0]?.value ? String(items[0].value) : "",
                    )
                  }
                />
              </div>
              <div className="mlm-entry-form__field">
                <Label required>Eklenecek Hurda</Label>
                <Input
                  type="number"
                  min="1"
                  value={addRejected}
                  onChange={setAddRejected}
                  placeholder="0"
                  disabled={saving}
                />
              </div>
            </div>

            <div className="mlm-entry-form__row">
              <div className="mlm-entry-form__field">
                <Label>Mevcut Hurda</Label>
                <div className="mlm-entry-form__readonly">
                  <strong>{currentRejected}</strong> adet
                </div>
              </div>
              <div className="mlm-entry-form__field">
                <Label>Günlük Üretim</Label>
                <div className="mlm-entry-form__readonly">
                  {loadingProduced ? (
                    "Hesaplanıyor..."
                  ) : (
                    <>
                      <strong>{totalProduced}</strong> adet
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mlm-entry-form__field">
              <Label>Not</Label>
              <Input
                value={notes}
                onChange={setNotes}
                placeholder="İsteğe bağlı not..."
                disabled={saving}
              />
            </div>
          </div>
        )}
        {error ? <div className="mlm-entry-form__error">{error}</div> : null}
      </ModalBody>
      <ModalFooter>
        <Button type="button" disabled={saving} onClick={onClose}>
          İptal
        </Button>
        <Button
          appearance="primary-animated"
          type="button"
          disabled={saving || loading || !canSave}
          onClick={handleSave}
        >
          {saving ? "Kaydediliyor..." : "Hurda Kaydet"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ScrapEntryModal;
